import client from "services/ngage/api-client";
import DataPage from "types/DataPage";
import {Site, SiteData, SiteDataAPI} from "types/Site";
import {FeatureCollection} from "geojson";
import deserializeSite from "services/ngage/deserializers/site";
import DataResource from "../../types/DataResource";
import {parameters} from "../string";
import {transformOpeningHoursForAPI} from "../opening-hours";

export type SiteFilters = {
    search?: string | null
    page?: number
    per_page?: number
}

/**
 * Get all site areas as a GeoJSON feature collection
 */
const areas = async (excludedIds?: Array<number>) => {
    const sites = await all()
    const filteredSites = sites?.data.filter(site => !excludedIds?.includes(site.id)) || []

    const colors = ['#8e44ad', '#2980b9', '#27ae60', '#16a085', '#d35400', '#c0392b', '#f1c40f'];

    return {
        type: "FeatureCollection",
        features: filteredSites.map(site => ({
            id: site.id,
            type: "Feature",
            properties: {
                type: 'Site',
                name: site.name,
                description: site.about,
                color: colors[(site.id || 0) % colors.length]
            },
            geometry: site.area
        }))
    } as FeatureCollection
}

/**
 * Get all sites (paginated)
 */
const all = async (page: number = 1) => {
    const response = await client.get<DataPage<Site> | null>(`/sites?page=${page}`)

    if(response.data){
        response.data.data = response.data.data.map(deserializeSite)
    }

    return response.data
}

/**
 * Get all sites that match the given filters (paginated)
 */
const filtered = async (filters: SiteFilters = {}) => {
    const params = parameters(filters, {page: 1, per_page: 100});
    const response = await client.get<DataResource<Site> | null>('/sites', {params});

    if (response.data) {
        response.data.data = response.data.data.map(deserializeSite)
    }

    return response.data
}

/**
 * Get a single site
 *
 * @param {number} id
 */
const get = async (id: number) => {
    const response = await client.get<any>(`/sites/${id}`)

    return deserializeSite(response.data)
}

/**
 * Store a new site
 *
 * @param data
 */
const store = async (data: SiteData) => {
    const hours = data.hours ? transformOpeningHoursForAPI(data.hours) : null;
    const siteData: SiteDataAPI = {...data, hours};

    const response = await client.post<any>('/sites', siteData)
    return deserializeSite(response.data)
}

/**
 * Update an existing site
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: SiteData) => {
    const hours = data.hours ? transformOpeningHoursForAPI(data.hours) : null;
    const siteData: SiteDataAPI = {...data, hours};

    const response = await client.put<Site>(`/sites/${id}`, siteData)
    return deserializeSite(response.data)
}

/**
 * Permanently delete a site
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    const response = await client.delete<null>(`/sites/${id}`)
    return response.status === 204
}

const sites = {
    all,
    filtered,
    areas,
    get,
    store,
    update,
    destroy
}

export default sites;